import { apiClient } from "@/lib/apiClient";

const BASE = "/api/admin/reports/settings";

/** Sunucu yanıtı `{ success, data }` sarmalayıcısı kullanabiliyor. */
function unwrap<T>(payload: unknown): T {
  const body = payload as { data?: T } | T;
  if (body && typeof body === "object" && "data" in (body as Record<string, unknown>)) {
    return (body as { data: T }).data;
  }
  return body as T;
}

/**
 * Liste yanıtlarını normalize eder.
 *
 * Backend'in TransformResponseInterceptor'ı `{ items, count }` döndüren uçları
 * açıp `data`yı DOĞRUDAN diziye çeviriyor ve `count`u üst seviyeye taşıyor.
 * Bu yüzden `data.items` okumak sessizce `undefined` veriyordu: kayıt başarıyla
 * oluşuyor ama liste boş görünüyordu. Her iki şekli de kabul ediyoruz.
 */
function unwrapList<T>(payload: unknown): T[] {
  const inner = unwrap<unknown>(payload);
  if (Array.isArray(inner)) return inner as T[];
  const items = (inner as { items?: unknown })?.items;
  return Array.isArray(items) ? (items as T[]) : [];
}

export interface ReportTablePreference {
  reportKey: string;
  /** Kolon anahtarlarının görüntülenme sırası. Boşsa raporun varsayılan sırası. */
  columnOrder: string[];
  hiddenColumns: string[];
}

export interface ReportFilterPreset {
  id: string;
  reportKey: string;
  name: string;
  filters: Record<string, unknown>;
  isDefault: boolean;
}

export type ReportScheduleFrequency = 0 | 1 | 2; // 0=Günlük, 1=Haftalık, 2=Aylık

export interface ReportSchedule {
  id: string;
  reportKey: string;
  name: string;
  format: "excel" | "pdf";
  frequency: ReportScheduleFrequency;
  hour: number;
  weekday: number | null;
  dayOfMonth: number | null;
  recipients: string[];
  filters: Record<string, unknown>;
  isActive: boolean;
  lastRunAt: string | null;
  lastError: string | null;
}

export interface SaveReportSchedulePayload {
  reportKey: string;
  name: string;
  format?: "excel" | "pdf";
  frequency: ReportScheduleFrequency;
  hour?: number;
  weekday?: number;
  dayOfMonth?: number;
  recipients: string[];
  filters?: Record<string, unknown>;
  isActive?: boolean;
}

export const reportSettingsApi = {
  // ── Tablo düzeni ──────────────────────────────────────────────
  getTablePreference: async (reportKey: string): Promise<ReportTablePreference> => {
    const { data } = await apiClient.get(`${BASE}/table/${reportKey}`);
    return unwrap<ReportTablePreference>(data);
  },

  saveTablePreference: async (
    reportKey: string,
    payload: { columnOrder: string[]; hiddenColumns: string[] },
  ): Promise<ReportTablePreference> => {
    const { data } = await apiClient.put(`${BASE}/table/${reportKey}`, payload);
    return unwrap<ReportTablePreference>(data);
  },

  resetTablePreference: async (reportKey: string): Promise<void> => {
    await apiClient.delete(`${BASE}/table/${reportKey}`);
  },

  // ── Kayıtlı filtre setleri ────────────────────────────────────
  listPresets: async (reportKey: string): Promise<ReportFilterPreset[]> => {
    const { data } = await apiClient.get(`${BASE}/presets`, { params: { reportKey } });
    return unwrapList<ReportFilterPreset>(data);
  },

  savePreset: async (
    reportKey: string,
    payload: { name: string; filters: Record<string, unknown>; isDefault?: boolean },
  ): Promise<ReportFilterPreset> => {
    const { data } = await apiClient.post(`${BASE}/presets/${reportKey}`, payload);
    return unwrap<ReportFilterPreset>(data);
  },

  deletePreset: async (presetID: string): Promise<void> => {
    await apiClient.delete(`${BASE}/presets/${presetID}`);
  },

  // ── Zamanlanmış e-posta raporları ─────────────────────────────
  listSchedules: async (): Promise<ReportSchedule[]> => {
    const { data } = await apiClient.get(`${BASE}/schedules`);
    return unwrapList<ReportSchedule>(data);
  },

  createSchedule: async (payload: SaveReportSchedulePayload): Promise<ReportSchedule> => {
    const { data } = await apiClient.post(`${BASE}/schedules`, payload);
    return unwrap<ReportSchedule>(data);
  },

  updateSchedule: async (
    scheduleID: string,
    payload: Partial<SaveReportSchedulePayload>,
  ): Promise<ReportSchedule> => {
    const { data } = await apiClient.patch(`${BASE}/schedules/${scheduleID}`, payload);
    return unwrap<ReportSchedule>(data);
  },

  deleteSchedule: async (scheduleID: string): Promise<void> => {
    await apiClient.delete(`${BASE}/schedules/${scheduleID}`);
  },
};
